import { WhoSeesWhatCard, AiUsageCard, DataMapCard } from "@/components/security/PrivacyCentreCards";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Lock, Timer, KeyRound, EyeOff, Compass } from "@/lib/icons";
import { MfaCard } from "@/components/security/MfaCard";
import { DataExportCard, DeleteAccountCard } from "@/components/security/DataCards";
import { AccessReceiptsCard } from "@/components/security/AccessReceiptsCard";
import { AiChoicesCard } from "@/components/security/AiChoicesCard";
import { LinkClinicianCard } from "@/components/security/LinkClinicianCard";

const FACTS = [
  { icon: Lock, text: "Notes, reflections, journal entries, tracker names and coach messages are encrypted (AES-256) before they're stored." },
  { icon: Timer, text: "You're signed out automatically after 15 minutes without activity. Sessions expire after 30 minutes." },
  { icon: KeyRound, text: "Every view of your health information by a coach is recorded in an access log you can download." },
  { icon: EyeOff, text: "The AI that writes your rewards only ever receives anonymous, category-level signals — never your name, notes or free text." },
];

export default function Security() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const replayTour = async () => {
    const role = user?.role === "clinician" ? "clinician" : "client";
    try { await api.post(`/users/tour/${role}/reset`); } catch { /* local reset still works */ }
    setUser((u) => (u ? { ...u, tours_done: { ...(u.tours_done || {}), [role]: false } } : u));
    navigate(role === "clinician" ? "/clinician" : "/client");
  };
  const [params] = useSearchParams();
  const required = params.get("required") === "1" || user?.mfa?.blocked;
  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-8 py-8 space-y-6 relative z-10" data-testid="security-page">
      {required && !user?.mfa_enabled && (
        <div className="rounded-2xl bg-red-50 border border-red-200 p-4 text-sm text-red-900" data-testid="mfa-required-notice">
          <strong>Two-step verification is required.</strong> Because you can see member health information, your account must use a second factor. Set it up below — it takes about a minute — and you'll be back in.
        </div>
      )}
      <div>
        <div className="text-xs font-bold uppercase tracking-widest text-amber-800">Account</div>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-stone-900 mt-1">Security & your data</h1>
        <p className="text-stone-600 mt-2 max-w-2xl">Your health information is yours. Here's how we protect it, and the controls you have over it.</p>
        <button type="button" onClick={replayTour} data-testid="replay-tour" className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-amber-800 hover:text-amber-900">
          <Compass className="w-4 h-4" /> Show me around again
        </button>
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        {FACTS.map(({ icon: Icon, text }) => (
          <div key={text} className="flex items-start gap-3 rounded-2xl bg-stone-50 border border-stone-100 p-4 text-sm text-stone-700">
            <Icon className="w-4 h-4 text-emerald-700 mt-0.5 shrink-0" /> {text}
          </div>
        ))}
      </div>

      <WhoSeesWhatCard />
      <DataMapCard />
      <MfaCard />
      <AiChoicesCard />
      <AiUsageCard />
      {user?.role === "client" && <AccessReceiptsCard />}
      <LinkClinicianCard />
      <DataExportCard />
      <DeleteAccountCard />

      <p className="text-xs text-stone-500">Read the full <Link to="/privacy" className="underline hover:text-amber-800">privacy notice</Link>.</p>
    </div>
  );
}
